import * as React from "react";
import styled from "@emotion/styled";
import { Navigate, useLocation } from "react-router-dom";
import { scrollToTop } from "utils/scroll";
import {
  Button,
  Checkbox,
  FieldError,
  Form,
  H1,
  Label,
  Legend,
  Link,
  Radio,
  TagCheckbox,
  Text,
  TextArea,
  Select,
  AllCaps,
  PageHeader,
  Box,
  Container,
  RequiredText,
  Stack,
  H2,
  CharacterCounter,
  IconArrow,
} from "atoms";
import reactionMustHugSrc from "atoms/icons/reaction_must-hug-2.svg";
import reactionChefsKissSrc from "atoms/icons/reaction_chefs-kiss.svg";
import reactionMindBlowingSrc from "atoms/icons/reaction_mind-blowing.svg";
import reactionNeedItNowSrc from "atoms/icons/reaction_need-it-now.svg";
import reactionObsessedSrc from "atoms/icons/reaction_obsessed.svg";
import reactionOutThisWorldSrc from "atoms/icons/reaction_out-of-this-world.svg";
import reactionTakeMyMoneySrc from "atoms/icons/reaction_take-my-money.svg";
import reactionThoughtProvokingSrc from "atoms/icons/reaction_thought-provoking.svg";
import { constants } from "config";
import { useAuth } from "features/auth";
import { useProfilePoints } from "features/profile";
import { Artist } from "features/reviewable/types";
import { ReviewableSummary } from "features/reviewable/components/ReviewableSummary";
import { fieldOptions, validators } from "utils/form";
import { ArtistReview } from "../types";
import { useArtistReviewForm } from "../api/useArtistReviewForm";
import { useReview } from "../api/useReview";

import reviewStatusOptions from "../data/reviewStatusOptions.json";

type ArtistReviewProps = {
  artist: Artist;
  review?: ArtistReview;
};

const ratingIcons = {
  "Must Hug": reactionMustHugSrc,
  "Chef's kiss": reactionChefsKissSrc,
  "Mind-blowing": reactionMindBlowingSrc,
  "Need it now": reactionNeedItNowSrc,
  Obsessed: reactionObsessedSrc,
  "Out of this world": reactionOutThisWorldSrc,
  "Take my money": reactionTakeMyMoneySrc,
  "Thought-provoking": reactionThoughtProvokingSrc,
};
type RatingLabel = keyof typeof ratingIcons;

const artTags = [
  "Bright and Bold",
  "Dark and Deadly",
  "Detailed and Intricate",
  "Museum-Worthy",
  "Original and Creative",
  "Retro Appeal",
  "Sparks Joy",
  "Thought-Provoking",
];

const reviewMinLength = 140;
const reviewMaxLength = 4000;
const textFieldRequirements = {
  ...fieldOptions.required,
  ...fieldOptions.minLength(reviewMinLength),
  ...fieldOptions.maxLength(reviewMaxLength),
};

function ArtistReviewForm({ artist, review }: ArtistReviewProps) {
  const [reviewCompleted, setReviewCompleted] = React.useState<boolean>(false);

  const {
    user,
    roles: { canReview, showAdminFeatures },
  } = useAuth();

  const { data: balance } = useProfilePoints(user?.id ?? "");
  const canStakePoints =
    !!balance && balance.pointsBalance >= constants.pointReviewCost;

  const location = useLocation();

  // Check database - Rejected reviews are not in useReviews
  const { data: existingReview } = useReview(
    "ARTIST",
    artist.id,
    user?.id || ""
  );

  const {
    register,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    onSubmit,
    mutation: { isError: isSubmitError },
    watching: { artTagValues, forYouIf },
  } = useArtistReviewForm(artist.id, review);

  // Set the state when the review is successful. Otherwise the query hooks
  // refetch and the user gets an error that they have already reviewed. We
  // also need to scroll to the top in case the user scrolled down to view the
  // review.
  React.useEffect(() => {
    if (!isSubmitError && isSubmitSuccessful) {
      setReviewCompleted(true);
      scrollToTop();
    }
  }, [isSubmitError, isSubmitSuccessful]);

  if (user?.claimedArtistPageIds?.includes(artist.id))
    return (
      <Main>
        <PageHeader overlap>
          <H1>Artists can&apos;t review&nbsp;themselves</H1>
          <Text>But we appreciate your enthusiasm!</Text>
        </PageHeader>
        <Container width="xs">
          <Box fullWidth>
            <Text>
              In order to keep Artist reviews on <AllCaps>Hug</AllCaps> fair and
              honest, users are not allowed to leave reviews on their own Artist
              Profile.
            </Text>
            <Text>
              Thank you for your understanding and for being a part of the{" "}
              <AllCaps>Hug</AllCaps> community!
            </Text>
            <ReturnLink
              variant="primary"
              size="xs"
              to={`/artists/${artist.id}`}
            >
              <IconArrow dir="l" /> Back to your Artist Profile
            </ReturnLink>
          </Box>
        </Container>
      </Main>
    );

  if (reviewCompleted) {
    const newTotalApproved =
      (balance?.lifetimeEarnings || 0) + constants.pointsEarnedReviewApproved;

    return (
      <Main>
        <PageHeader overlap>
          <H1>Thank you for your&nbsp;review!</H1>
        </PageHeader>
        <Container width="xs">
          <Box fullWidth>
            <Text>
              You have spent {constants.pointReviewCost} <AllCaps>Hug</AllCaps>{" "}
              Points to submit your review.
            </Text>
            <Text>
              If your review is approved, you will receive{" "}
              {constants.pointsEarnedReviewApproved} <AllCaps>Hug</AllCaps>{" "}
              Points for a lifetime total of {newTotalApproved}.
            </Text>
            <ReturnLink
              variant="primary"
              size="xs"
              to={`/artists/${artist.id}`}
            >
              <IconArrow dir="l" /> Return to Artist
            </ReturnLink>
          </Box>
        </Container>
      </Main>
    );
  }

  if (!review && existingReview) {
    return (
      <Main>
        <PageHeader>
          <Text>
            {existingReview.status === "REJECTED"
              ? "You already have a rejected review for this artist."
              : "You have already reviewed this artist."}
          </Text>
        </PageHeader>
        <Container width="xs">
          <Box fullWidth>
            <Text>
              <ReturnLink
                variant="primary"
                size="xs"
                to={`/artists/${artist.id}`}
              >
                <IconArrow dir="l" /> Return to Artist
              </ReturnLink>
            </Text>
          </Box>
        </Container>
      </Main>
    );
  }

  if (!canReview) {
    return <Navigate to="/onboarding" replace state={{ from: location }} />;
  }

  return (
    <Main>
      {review ? (
        <PageHeader overlap>
          <H1 size="h2">Update Review</H1>
        </PageHeader>
      ) : (
        <PageHeader overlap>
          <H1 size="h2">Write a Review</H1>

          {canStakePoints && (
            <Container width="xs">
              <Text size="sm">
                Submitting a review costs {constants.pointReviewCost}{" "}
                <AllCaps>Hug</AllCaps> Points. Don&apos;t worry, you&apos;ll
                earn it all back (and&nbsp;more!) once your review
                is&nbsp;approved.
              </Text>
            </Container>
          )}
        </PageHeader>
      )}

      {!canStakePoints ? (
        <Container width="xs">
          <Box fullWidth>
            <SText size="md">
              Uh oh! You don&apos;t have enough <AllCaps>Hug</AllCaps> Points to
              write a review at the moment.
            </SText>

            <SText size="sm">
              <strong>Current Balance:</strong> {balance?.pointsBalance ?? 0}
            </SText>

            <ReturnLink
              variant="primary"
              size="xs"
              to={`/artists/${artist.id}`}
            >
              <IconArrow dir="l" /> Return to Artist
            </ReturnLink>
          </Box>
        </Container>
      ) : (
        <Container width="sm">
          <Box fullWidth>
            <SForm onSubmit={onSubmit}>
              <div>
                <H2 size="sm">Writing your Review</H2>
                <Text size="xs">
                  To nurture a supportive environment for artists and art
                  lovers, reviews ought to be descriptive, respectful, and true
                  to your own personal experience.
                </Text>
                <Text size="xs">
                  Your review may be rejected if it contains harmful or
                  offensive language, or looks like spam.
                </Text>
              </div>
              <SReviewableSummary
                reviewable={artist}
                showDescription={false}
                inline
              />

              {showAdminFeatures && (
                <Label>
                  Review Status
                  <Select {...register("status", { ...fieldOptions.required })}>
                    {reviewStatusOptions.map((status) => (
                      <option key={status} value={status.toUpperCase()}>
                        {status}
                      </option>
                    ))}
                  </Select>
                </Label>
              )}
              <Fieldset>
                <Legend size="sm">
                  How would you rate this artist?
                  <RequiredText />
                </Legend>
                <RatingGrid>
                  {Object.keys(ratingIcons).map((key) => (
                    <RatingRadio
                      {...register("rating", { ...fieldOptions.required })}
                      label={key}
                      value={key}
                      key={key}
                    />
                  ))}
                </RatingGrid>

                {errors.rating && (
                  <FieldError>Please choose a rating.</FieldError>
                )}
              </Fieldset>

              <Label>
                This artist may be for you if...
                <RequiredText />
                <TextArea
                  {...register("forYouIf", textFieldRequirements)}
                  isInvalid={!!errors.forYouIf}
                  aria-invalid={!!errors.forYouIf}
                />
                {errors.forYouIf && (
                  <FieldError>{errors.forYouIf.message}</FieldError>
                )}
                <CharacterCounter
                  min={reviewMinLength}
                  max={reviewMaxLength}
                  value={forYouIf}
                />
              </Label>

              <Fieldset>
                <Legend size="sm">
                  Describe the Art <small>Select up to 2</small>
                </Legend>
                {artTags.map((value) => (
                  <TagCheckbox
                    variant="art"
                    {...register("artTags", {
                      validate: validators.limitReviewTags,
                    })}
                    label={value}
                    value={value}
                    key={value}
                    disabled={
                      artTagValues?.length >= 2 &&
                      !artTagValues?.includes(value)
                    }
                  />
                ))}
                {errors.founderTeamTags && (
                  <FieldError block>
                    {errors.founderTeamTags.message}
                  </FieldError>
                )}
              </Fieldset>
              <div>
                <SCheckbox
                  label="I have purchased this artist's work"
                  {...register("reviewerMinted")}
                />
                <SCheckbox
                  label="I currently hold this artist's work"
                  {...register("reviewerHolding")}
                />
              </div>

              <Fieldset>
                <Legend size="sm">
                  How would you like your name to be displayed on the review?
                </Legend>
                <Stack>
                  <SRadio
                    {...register("isAnonymous")}
                    value="false"
                    label={
                      <>
                        Use my display name (
                        {review ? review.displayName : user?.name})
                      </>
                    }
                  />
                  <SRadio
                    {...register("isAnonymous")}
                    value="true"
                    label="Hide my display name (Anonymous user)"
                  />
                </Stack>
              </Fieldset>

              <div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                  arrow="r"
                >
                  Submit Review
                </Button>

                {isSubmitError && (
                  <FieldError block>
                    Sorry, something went wrong. Try again.
                  </FieldError>
                )}

                <SText size="xxs">
                  <em>
                    By submitting this review, I confirm this review is based on
                    my own personal experience, and agree to spend{" "}
                    {constants.pointReviewCost}&nbsp;<AllCaps>Hug</AllCaps>
                    &nbsp;Points.
                  </em>
                </SText>
              </div>
            </SForm>
          </Box>
        </Container>
      )}
    </Main>
  );
}

const Fieldset = styled(Label)({}).withComponent("fieldset");

const Main = styled.main({
  padding: "0 0 3rem 0",
});

const SForm = styled(Form)({
  gap: "2rem",
});

const SText = styled(Text)({
  margin: "1em 0",
});
const RatingGrid = styled.div({
  display: "grid",
  placeItems: "center",
  gap: "1.5em 2px",
  gridTemplateColumns: "repeat(auto-fit, minmax(92px, 1fr))",
  "@media (min-width: 480px)": {
    placeItems: "start",
    gridTemplateColumns: "repeat(4, 1fr)",
  },
  "@media (min-width: 831px)": {
    gridTemplateColumns: "repeat(8, 1fr)",
  },
});
const RatingRadio = styled(Radio)(({ label, theme }) => ({
  position: "relative",
  flexDirection: "column",
  alignItems: "center",
  flex: "1 1 70px",
  gap: "5px",
  marginBottom: 0,
  maxWidth: "120px",
  width: "100%",
  textAlign: "center",
  justifyContent: "flex-start",
  input: {
    border: `2px solid ${theme.colors.fg20}`,
    height: 60,
    width: 60,
    "@media (prefers-reduced-motion: no-preference)": {
      transition: "all 0.15s ease",
    },
    backgroundColor: theme.colors.bg,
  },
  "input:hover, input:focus-visible": {
    backgroundColor: theme.colors.bg,
    border: `2px solid ${theme.colors.fg}`,
  },
  "input::after": {
    content: "none",
  },
  "input::before": {
    all: "unset",
    backgroundImage: `url("${ratingIcons[label as RatingLabel]}")`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    content: "''",
    clipPath: "none",
    width: 50,
    height: 50,
    pointerEvents: "none",
    transform: "translate(2%, 2%)",
    "@media (prefers-reduced-motion: no-preference)": {
      transition: "all 0.15s ease",
    },
  },
  "input:checked": {
    backgroundColor: theme.colors.bg,
  },
  div: {
    ...theme.fonts.body,
    border: "1px solid rgba(0,0,0,0)",
    borderRadius: theme.borderRadius.round,
    fontSize: 13,
    padding: "2px 8px 1px",
    "@media (prefers-reduced-motion: no-preference)": {
      transition: "all 0.15s ease",
    },
    lineHeight: 1,
  },
  "input:checked + div": {
    borderColor: theme.colors.fg,
  },
  "@media (min-width: 400px)": {
    input: {
      height: 70,
      width: 70,
    },
    "input::before": {
      width: 60,
      height: 60,
    },
  },
}));
const ReturnLink = styled(Link)({
  margin: "2rem 0 0",
});
const SReviewableSummary = styled(ReviewableSummary)(({ theme }) => ({
  margin: "1em auto 0",
  img: {
    borderRadius: theme.borderRadius.round,
  },
}));

const SCheckbox = styled(Checkbox)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: ".875rem",
  color: theme.colors.fg,
}));

const SRadio = styled(Radio)(({ theme }) => ({
  fontSize: ".875rem",
  color: theme.colors.fg,
}));
export { ArtistReviewForm };
